import React from 'react';
import Icon from '../AppIcon';

const ProgressIndicator = ({ 
  streakDays = 0, 
  versesMemorized = 0, 
  weeklyGoal = 7,
  className = '',
  variant = 'default' // 'default', 'compact', 'detailed'
}) => {
  const progressPercentage = Math.min((versesMemorized / weeklyGoal) * 100, 100);

  if (variant === 'compact') {
    return (
      <div className={`flex items-center space-x-2 ${className}`}>
        <div className="flex items-center space-x-1 px-2 py-1 bg-success/10 rounded-md">
          <Icon name="Flame" size={14} color="var(--color-success)" />
          <span className="text-xs font-mono text-success">{streakDays}d</span>
        </div>
      </div>
    );
  }

  if (variant === 'detailed') {
    return (
      <div className={`bg-card rounded-lg p-4 shadow-warm ${className}`}>
        <div className="space-y-4">
          {/* Streak Section */}
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-2">
              <div className="flex items-center justify-center w-8 h-8 bg-success/20 rounded-lg">
                <Icon name="Flame" size={18} color="var(--color-success)" />
              </div>
              <div>
                <p className="text-sm font-medium text-foreground">Daily Streak</p>
                <p className="text-xs text-muted-foreground">Keep it going!</p>
              </div>
            </div>
            <div className="text-right">
              <p className="text-2xl font-mono font-semibold text-success">{streakDays}</p>
              <p className="text-xs text-muted-foreground">days</p>
            </div>
          </div>

          {/* Weekly Progress */}
          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <p className="text-sm font-medium text-foreground">Weekly Goal</p>
              <p className="text-sm font-mono text-muted-foreground">
                {versesMemorized}/{weeklyGoal}
              </p>
            </div>
            <div className="w-full bg-muted rounded-full h-2">
              <div
                className="bg-success h-2 rounded-full transition-all duration-300"
                style={{ width: `${progressPercentage}%` }}
              />
            </div>
          </div>

          {/* Achievement Badges */}
          <div className="flex items-center space-x-2">
            <div className="flex items-center space-x-1 px-2 py-1 bg-accent/10 rounded-md">
              <Icon name="Award" size={14} color="var(--color-accent)" />
              <span className="text-xs font-caption text-accent">Consistent</span>
            </div>
            {streakDays >= 7 && (
              <div className="flex items-center space-x-1 px-2 py-1 bg-warning/10 rounded-md">
                <Icon name="Star" size={14} color="var(--color-warning)" />
                <span className="text-xs font-caption text-warning">Week Warrior</span>
              </div>
            )}
          </div>
        </div>
      </div>
    );
  }

  // Default variant
  return (
    <div className={`flex items-center space-x-4 ${className}`}>
      {/* Streak Display */}
      <div className="flex items-center space-x-2 px-3 py-2 bg-success/10 rounded-lg">
        <Icon name="Flame" size={16} color="var(--color-success)" />
        <div className="text-center">
          <p className="text-lg font-mono font-semibold text-success">{streakDays}</p>
          <p className="text-xs font-caption text-success">day streak</p>
        </div>
      </div>

      {/* Weekly Progress */}
      <div className="flex-1 min-w-0">
        <div className="flex items-center justify-between mb-1">
          <p className="text-sm font-medium text-foreground">This Week</p>
          <p className="text-sm font-mono text-muted-foreground">
            {versesMemorized}/{weeklyGoal}
          </p>
        </div>
        <div className="w-full bg-muted rounded-full h-2">
          <div
            className="bg-primary h-2 rounded-full transition-all duration-300"
            style={{ width: `${progressPercentage}%` }}
          />
        </div>
      </div>

      {/* Achievement Icon */}
      {progressPercentage >= 100 && (
        <div className="flex items-center justify-center w-8 h-8 bg-accent/20 rounded-lg">
          <Icon name="Trophy" size={18} color="var(--color-accent)" />
        </div>
      )}
    </div>
  );
};

export default ProgressIndicator;